import {
  BlockScroll,
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { cn } from "cn";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button/button";
import { BwCheckCircleLine, BwCloseCircleLine } from "@/components/ui/icons";

type Status = "processing" | "success" | "failed";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: Status;
  onCtaClick?: () => void;
  onSuccess?: () => void;
  className?: string;
};

export const PaymentStatusModal = ({
  open,
  onOpenChange,
  status,
  onCtaClick,
  onSuccess,
  className,
}: Props) => {
  // auto-close on success if requested
  useEffect(() => {
    if (!open || status !== "success") return;
    const t = setTimeout(() => {
      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    }, 1500);
    return () => clearTimeout(t);
  }, [open, status, onOpenChange, onSuccess]);

  const copy = useMemo(() => getCopy(status), [status]);
  return (
    <Modal
      open={open}
      onOpenChange={(value) => {
        if (status === "processing" || status === "success") return;
        onOpenChange(value);
      }}
    >
      <ModalContent
        dialogProps={{ showCloseButton: false }}
        drawerProps={{ showCloseButton: false, showHandleButton: false }}
        className={cn("w-full sm:max-w-xs rounded-2xl p-0", className)}
      >
        <ModalHeader className="sr-only">
          <ModalTitle>Payment Status</ModalTitle>
          <ModalDescription>Payment Status</ModalDescription>
        </ModalHeader>
        <BlockScroll>
          <div className="flex flex-col items-center gap-1 p-4 pt-1 text-center sm:p-5">
            {/* Icon */}
            <StatusIcon status={status} />

            {/* Title */}
            <h3 className="mt-4 text-base font-medium leading-normal">
              {copy.title}
            </h3>

            {/* Description */}
            {copy.description && (
              <p className="text-sm font-normal leading-5 text-text-dark-high">
                {copy.description}
              </p>
            )}

            {/* CTA for failed or custom */}
            {copy.ctaLabel && (
              <div className="mt-4">
                <Button size="lg" onClick={onCtaClick}>
                  {copy.ctaLabel}
                </Button>
              </div>
            )}
          </div>
        </BlockScroll>
      </ModalContent>
    </Modal>
  );
};

function StatusIcon({ status }: { status: Status }) {
  if (status === "processing") {
    return (
      <Loader2
        className="h-12 w-12 animate-spin text-text-primary"
        aria-hidden
      />
    );
  }
  if (status === "success") {
    return (
      <BwCheckCircleLine className="h-12 w-12 fill-fill-primary" aria-hidden />
    );
  }
  return (
    <BwCloseCircleLine className="h-12 w-12 fill-fill-negative" aria-hidden />
  );
}

function getCopy(status: Status) {
  if (status === "processing") {
    return {
      title: "Processing your payment...",
      description: "Hang tight while we process your payment",
      ctaLabel: undefined as string | undefined,
    };
  }
  if (status === "success") {
    return {
      title: "Payment successful!",
      description: "You'll be redirected shortly.",
      ctaLabel: undefined as string | undefined,
    };
  }
  // failed
  return {
    title: "Oh no, your payment failed",
    description:
      "Sorry, your payment was not successful. Please check details and try again.",
    ctaLabel: "Update payment details",
  };
}
